import { toValue, type MaybeRefOrGetter } from '@vueuse/core';
import { MonetizationType, ObjectType, Platform, type SportType } from '@/@types/graphql-types';
import { BuyboxParam, OfferLocation } from '@/helpers/offer-helper';
import type { SnowplowContext, ContextClasses } from '@/helpers/tracking/providers';
import type {
	ClickoutContentType,
	ClickoutOfferDetails,
	ClickoutPlacement,
	ClickoutType,
} from '@/interfaces/snowplow/clickout-context';

import { base64EncodeUrl, btoa } from '@/helpers/base64-helper';
import { isClickoutOfferDetails } from '@/interfaces/snowplow/clickout-context';
import { SnowplowClickoutContextGraphql, SnowplowLinkContext } from '@/helpers/tracking/providers';
import { TrackingAppId, TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { captureMessageForSentry } from '@/helpers/sentry-helper';

import { useMutation } from '@/helpers/composables/useApollo';

import {
	type GetFinalClickoutUrlMutation,
	type GetFinalClickoutUrlMutationVariables,
	GetFinalClickoutUrlDocument,
} from '@/graphql/mutation/GetFinalClickoutUrl.mutation';

import { getTCString } from './impression-tracker-helper';
import { GDPR_WEB_LOCALES } from '@/constants/web-locales.constant';
import type { WebLocale } from '@/enums/web-locale';
import type { Maybe } from '@/helpers/types';
import { setYieldOptimisationTags } from '@/helpers/promotion-helper';

import { BuyBoxFilterOptions } from '@/stores/user.store';
import { generateUUIDv4 } from './uuid-helper';
import { flushBatchedImpressions } from './composables/useBatchImpressionTracking';
import { AdServerIds } from '@/helpers/composables/useAdServer';
import { setTubiAffiliationTags } from '@/components/experiments/TubiTest/tubiAffiliation';

/**
 * Wrapper around the built-in URL class with a fluent interface for setting
 * query parameters that safely ignores falsy values.
 */
export interface UrlHelper<Parameter = string> {
	/** Reference to the underlying URL class. */
	url: URL;

	/** Holds any error that may have happened during instantiation. */
	error: Error | null;

	type: 'ClassicUrl' | 'ClickoutUrl';

	/** Get back the value of a parameter on the URL Helper. */
	get(parameter: Parameter): string | null;

	/**
	 * Set a URL parameter, if the value is falsy, the parameter will not be added.
	 * **Note that parameter values are automatically encoded.**
	 */
	set<Value extends Maybe<string | number>>(parameter: Parameter, value: Value): UrlHelper<Parameter>;

	/** Delete a URL parameter. If the parameter does not exist, it does nothing. */
	unset(parameter: Parameter): UrlHelper<Parameter>;

	/**
	 * Group and apply multiple common methods that should be applied to one ore more URLs.
	 *
	 * Order of methods
	 * apply
	 * config(skypUCT2, setFreeTrial, etc..)
	 * set on Url
	 * set on Offer
	 *
	 *
	 * @example
	 * function urlTracking(u: UrlHelper) {
	 * 	return u
	 * 		.set('utm_source', 'google')
	 * 		.set('country', contry)
	 * }
	 *
	 * new UrlHelper('www.example.com')
	 * 	.apply(urlTracking)
	 *  .setFreeTrial()
	 * 	.set('uct_buybox', 'sport')
	 *
	 */
	apply(transform: (url: UrlHelper) => UrlHelper): UrlHelper;

	/** Returns the URL as a string. */
	toString(): string;
}

/** Assert that a UrlHelper class is a ClickoutUrl. */
export function isClickoutUrl(url: UrlHelper): url is ClickoutUrl {
	return url.type === 'ClickoutUrl';
}

/** Assert that a UrlHelper class is a ClassicUrl. */
export function isClassicUrl(url: UrlHelper): url is ClassicUrl {
	return url.type === 'ClassicUrl';
}

export class ClassicUrl implements UrlHelper {
	#url: URL;
	#error: Error | null = null;

	#type = 'ClassicUrl' as const;

	/**
	 * A helper class around the built-in URL class that offers a fluent
	 * interface (method chaining) for setting query parameters. It
	 * safely ignores null or empty query param values.
	 *
	 * @param urlString A valid string URL or exisiting URL class.
	 */
	constructor(urlString: Maybe<string | URL>) {
		try {
			if (urlString == null) {
				throw new Error('[ClassicUrl] "urlString" must be a string and valid URL.');
			}

			this.#url = new URL(urlString);
		} catch (e: any) {
			console.warn(e.message ?? '[ClassicUrl] The URL String passed to URL Helper is an invalid URL');
			this.#error = e;
		}
	}

	/** Confirms this is a ClassicUrl. */
	get type() {
		return this.#type;
	}

	/** Reference to the underlying URL class. */
	get url() {
		return this.#url;
	}

	/** Holds any error that may have happened during instantiation. */
	get error() {
		return this.#error;
	}

	/** Get back the value of a parameter on the Classic URL. */
	get(parameter: string): string | null {
		return this.#url.searchParams.get(parameter);
	}

	/** Set a URL parameter, if the value is falsy, the parameter will not be added. */
	set(parameter: string, value: Maybe<string | number>): ClassicUrl {
		if (value) {
			this.#url.searchParams.set(parameter, value.toString());
		}

		return this;
	}

	unset(parameter: string): ClassicUrl {
		this.#url.searchParams.delete(parameter);
		return this;
	}

	apply(transform: (url: ClassicUrl) => ClassicUrl) {
		return transform(this);
	}

	/** Returns the Classic URL as a string. */
	toString() {
		return this.#url.toString();
	}
}

// JustWatch UCT with a different subdomain to bypass ad blockers
const CLICK_URL = 'https://e.justwatch.com' as const;

// for WOW, WOW Sport, Sky Go, and Sky Sport we need GDPR information
const WOW_PROVIDER_IDS = [29, 703, 30];

type GDPRProviderConfig = {
	providers: number[];
	bundles: number[];
};

const GDPR_PROVIDERS_CONFIG: Record<string, GDPRProviderConfig> = {
	de: { providers: WOW_PROVIDER_IDS, bundles: [] },
	fr: { providers: [345], bundles: [39] },
	it: { providers: [], bundles: [2] },
};

type GDPRCountries = keyof typeof GDPR_PROVIDERS_CONFIG;

/**
 * TC String is only made after consents have been obtained
 * Since this is only for one provider in one country, this is the tc string when all consents are denied.
 */
const NO_CONSENT_TC_STRING =
	'CQF3YIAQF3YIAAFADBDEBJFgAAAAAEPgAAYgAAAQuAIAFCIgAKIAICCAAAAEAAgrCAigAAAAAECBAAAECABwAgEokIEAAAAAAAAAAABAgACAAAAABCIAAAAAAAAAABAAAgAACAQAECAAAACgAAgABAQAAAAAAAAAAAIAAgBAACAQAAEAIAAAABAEAAAAQACAAEAAAAAIAAQAAAAAAAAAACAAAQIYAAAA';

type Offer = Maybe<{
	standardWebURL?: Maybe<string>;
	preAffiliatedStandardWebURL?: Maybe<string>;
	package?: Maybe<{ packageId?: number }>;
}>;
type ClickoutUrlParam =
	| 'cx'
	| 'r'
	| 'sid'
	| 'uct_buybox'
	| 'uct_country'
	| 'uct_ct'
	| 'uct_ua'
	| 'uct_title'
	| 'uct_itsct'
	| 'uct_sport'
	| 'uct_tryout'
	| 'uct_web_app_version'
	| 'utm_campaign'
	| 'utm_medium'
	| 'utm_source'
	| 'uct_sr'
	| 'uct_ft'
	| 'uct_pt'
	| 'uct_ctag'
	| 'uct_ctag2'
	| 'uct_aid'
	| 'uct_bid'
	| 'uct_cid';

interface ClickoutTrackingProperties {
	offerLocation?: OfferLocation;
	contentType?: ClickoutContentType.LeavingSoon;
	filterOption?: BuyBoxFilterOptions;
	rowNumber?: number;
	clickoutType?: ClickoutType;
	placement?: ClickoutPlacement;
	jwRawEntityId?: string;
}

type DeferredPromise<T> = {
	promise: Promise<T>;
	resolve: (value: T) => void;
	reject: (reason?: any) => void;
};

export class ClickoutUrl implements UrlHelper<ClickoutUrlParam> {
	#url: ClassicUrl;
	#error: Error | null = null;

	#titleSourceId: string | null = null;

	#provider: ClickoutOfferDetails | null = null;

	#type = 'ClickoutUrl' as const;

	#skipRedirect: boolean = false;

	#isFreeTrial: boolean = false;

	#preAffiliatedUrl: string | null = null;

	/** skipYieldOptimization - boolean to bypass yield optimisation tags */
	#skipYieldOptimization: boolean = false;

	/** deeplink - finalUrl after affiliation is applied, to be attached to clickoutContext  */
	#deeplink: string | null = null;

	/** uuid for linkContext for event interlinking - attached to the offer impression (type: self) and clickout event (type: impression) */
	#offerImpressionUUID: string;

	/** uuid for linkContext for event interlinking - attached to the clickout event (type: self) */
	#clickoutUUID: string;

	/**
	 * Create a promise that will be resolved when the offerImpressionUUID is updated.
	 * This prevents clickout event from firing before the batch impression queue is processed.
	 * */
	#offerImpressionUUIDDeferred: DeferredPromise<string> = createDeferred<string>();

	/** triggerRef()
	 * This is a reference to a function that can be called to trigger a reactivity update from certain methods in the class.
	 * This is used to update the reactive state of the ClickoutUrl instance when certain properties change.
	 * This is required because of the way Vue reactivity works with class instances.
	 */
	#triggerRef: (() => void) | null = null;

	/**
	 * A helper class for Clickout URLs (links that pass through JW UCT).
	 * It uses a fluent interface (method chaining) and handles adding contexts,
	 * default tracking, and null or empty parameter values
	 *
	 * @param offer Any object that, at least, has "standardWebURL"
	 */
	constructor(_offer: MaybeRefOrGetter<Offer | (Offer & ClickoutOfferDetails)>) {
		const clickUrl = JW_CONFIG.CLICK_URL.includes('moviecycle') ? JW_CONFIG.CLICK_URL : CLICK_URL;

		this.#url = new ClassicUrl(`${clickUrl}/a`);
		this.#error = this.#url.error;
		// make the uuidVersion reactive, so that we can update

		const offer = toValue(_offer);

		// adds app version information to the URL as a query parameter
		this.set('uct_web_app_version', TrackingAppId);

		// May need to be encoded due to click tags provided by clients
		this.set('r', offer?.standardWebURL ?? '');

		// Create clickoutUUID on initialisation. This is updated before each clickout event, which in turn triggers a reactive update when using setTriggerRef()
		this.setClickoutUUID();

		if (offer?.preAffiliatedStandardWebURL) {
			this.#preAffiliatedUrl = offer.preAffiliatedStandardWebURL as string;
		}

		if (!this.#skipYieldOptimization) {
			// Yield optimisation tags
			setYieldOptimisationTags(this, offer?.package?.packageId ?? 0);
			setTubiAffiliationTags(this, offer?.package?.packageId ?? 0);
		}

		const pageTypeContext = TrackingHelper.getPageTypeContext();
		const titleContext = TrackingHelper.getAdditionalContexts().find(
			(context: SnowplowContext) => context.__name === 'TitleContextGraphql'
		);
		if (titleContext && offer?.package?.packageId === 350) this.set('uct_title', (titleContext as any).jwEntityId);

		// Tracking Contexts
		this.addContexts(TrackingHelper.getAdditionalContexts());

		this.addContexts(pageTypeContext);

		if (isClickoutOfferDetails(offer)) {
			this.#provider = offer;
			this.addContexts(SnowplowClickoutContextGraphql.fromProviderOffer(offer, undefined, undefined));
		}
	}

	/** Confirms this is a ClickoutUrl. */
	get type() {
		return this.#type;
	}

	/** Reference to the underlying URL class. */
	get url() {
		return this.#url.url;
	}

	/** Returns any error that may have happened during instantiation. */
	get error() {
		return this.#error;
	}

	get titleSourceId() {
		return this.#titleSourceId;
	}

	get provider() {
		return this.#provider;
	}

	get providerId() {
		return this.#provider?.package?.packageId;
	}

	get providerClearName() {
		return this.#provider?.package?.clearName;
	}

	get hasSkipRedirect() {
		return this.#skipRedirect;
	}

	get preAffiliatedUrl() {
		return this.#preAffiliatedUrl;
	}

	get isFreeTrial() {
		return this.#isFreeTrial;
	}

	get bundleId() {
		return this.contexts.PackageContext?.parentId ?? null;
	}

	get deeplink() {
		return this.#deeplink;
	}

	get offerImpressionUUID() {
		return this.#offerImpressionUUID;
	}

	/** A promise that resolves when the offer impression UUID is set. */
	get offerImpressionUUIDPromise(): Promise<string> {
		return this.#offerImpressionUUIDDeferred.promise;
	}

	/** Get back the value of a parameter on the Clickout URL. */
	get(parameter: ClickoutUrlParam) {
		return this.#url.get(parameter);
	}

	/**
	 * Set a URL parameter, if the value is falsy, the parameter will not be added.
	 * **Note that parameter values are automatically encoded.**
	 */
	set(parameter: 'r', value: string): ClickoutUrl;
	set(parameter: 'cx', value: string): ClickoutUrl;
	/** Domain session ID */
	set(parameter: 'sid', value: Maybe<string>): ClickoutUrl;
	set(parameter: 'uct_buybox', value: BuyboxParam): ClickoutUrl;
	set(parameter: 'uct_country', value: string): ClickoutUrl;
	set(parameter: 'uct_ct', value: Maybe<string>): ClickoutUrl;
	set(parameter: 'uct_ua', value: Maybe<string>): ClickoutUrl;
	set(parameter: 'uct_title', value: Maybe<string>): ClickoutUrl;
	set(parameter: 'uct_itsct', value: Maybe<string>): ClickoutUrl;
	set(parameter: 'uct_sport', value: Maybe<SportType>): ClickoutUrl;
	set(parameter: 'uct_tryout', value: number): ClickoutUrl;
	set(parameter: 'utm_campaign', value: string): ClickoutUrl;
	set(parameter: 'utm_medium', value: string): ClickoutUrl;
	set(parameter: 'utm_source', value: string): ClickoutUrl;
	set(parameter: 'uct_web_app_version', value: string): ClickoutUrl;
	set(parameter: 'uct_sr', value: string): ClickoutUrl;
	set(parameter: 'uct_ft', value: string): ClickoutUrl;
	set(parameter: 'uct_pt', value: 'movie' | 'show' | 'season'): ClickoutUrl;
	/** uct_ctag, uct_ctag2
	 * allows us to pass the campaignTag, campaignTag2 field for UCT2Input via UCT Hijack (jw-uct)
	 * these fields allow FE to pass campaignTag(s) (e.g. subId3, ct, tag) for experiments, qtv_compositionId etc.. where the tag is difficult to set without the need for additional YAML rules
	 * */
	set(parameter: 'uct_ctag', value: Maybe<string>): ClickoutUrl;
	set(parameter: 'uct_ctag2', value: Maybe<string>): ClickoutUrl;
	// AdServer IDs from getAd mutation - aid (adId), bid (bidId), cid (campaignId)
	set(parameter: 'uct_aid', value: Maybe<number>): ClickoutUrl;
	set(parameter: 'uct_bid', value: Maybe<string>): ClickoutUrl;
	set(parameter: 'uct_cid', value: Maybe<number>): ClickoutUrl;
	set(parameter: ClickoutUrlParam, value: Maybe<string | number>): ClickoutUrl {
		this.#url.set(parameter, value?.toString());

		return this;
	}

	unset(parameter: string): ClickoutUrl {
		this.#url.unset(parameter);
		return this;
	}

	/** Set a URL parameter but directly on the redirect offer link. */
	setOnOffer(parameter: string, value: Maybe<string | number>): ClickoutUrl {
		const offer = this.get('r');
		if (value == null || offer === null) return this;

		return this.set('r', new ClassicUrl(offer).set(parameter, value).toString());
	}

	#trackingContexts = {} as ContextClasses;

	/** The tracking contexts saved on the Clickout URL. */
	get contexts() {
		return this.#trackingContexts;
	}

	#trackingProperties = {} as ClickoutTrackingProperties;

	get trackingProperties() {
		return this.#trackingProperties;
	}

	/** Prevents params being overwritten with user agent params */
	skipYieldOptimization(skipYO: boolean = true) {
		this.#skipYieldOptimization = skipYO;
		if (skipYO === true) this.unset('uct_ua');

		return this;
	}

	/** Skips the UCT redirect & opens the URL directly */
	skipRedirect(skipRedirect: boolean = true) {
		this.#skipRedirect = skipRedirect;
		return this;
	}

	/** set canary environment when available */
	setCanary(value?: string | null) {
		if (!value?.includes('canary')) return this;

		// @ts-expect-error - uct_canary is only a temporary parameter for UCT2 migration testing. Not adding to types, as this should not be used in future.
		return this.set('uct_canary', value);
	}

	/** Updates ClickoutContext deeplink field, and prevents mutation from firing more than once */
	setDeeplink(deeplink: string) {
		this.#deeplink = deeplink;
		return this;
	}

	/** Page Type can be different to the titleCntext objectType that is extracted by UCT2 Hijack (e.g. for shows), and need to be explicitly set for certain tags */
	setPageType(value: ObjectType) {
		if (!['MOVIE', 'SHOW', 'SHOW_SEASON'].includes(value)) return this;

		const pageTypeLower = value.toLowerCase() as 'movie' | 'show' | 'show_season';
		const pageType = pageTypeLower === 'show_season' ? 'season' : pageTypeLower;

		return this.set('uct_pt', pageType);
	}

	/** Sets Ad Server IDs (adId, bidId, campaignId) on the Clickout URL */
	setAdIds(adIds?: AdServerIds | null) {
		if (adIds == null) return this;

		const { bidId, adId, campaignId } = adIds;

		if (adId) this.set('uct_aid', adId);
		if (bidId) this.set('uct_bid', bidId);
		if (campaignId) this.set('uct_cid', campaignId);

		return this;
	}

	/** Sets UCT1 uct_ft param and updates UCT2 params */
	setFreeTrial(isFreeTrial: boolean = true) {
		this.#isFreeTrial = isFreeTrial;
		return this.set('uct_ft', isFreeTrial ? '1' : '0');
	}

	/** @deprecated */
	setUCTHijackParams({ isQTVFallback = false, titleIdOrHash }: { isQTVFallback?: boolean; titleIdOrHash: string }) {
		// @ts-expect-error
		return this.set('uct_qtv_fb', isQTVFallback ? 1 : 0).set('uct_qtv_id', titleIdOrHash);
	}

	setTitleSourceId(titleSourceId: string | null) {
		this.#titleSourceId = titleSourceId;
		return this;
	}

	setForTracking<P extends keyof ClickoutTrackingProperties, V extends ClickoutTrackingProperties[P]>(
		property: P,
		value: V
	) {
		this.#trackingProperties[property] = value;
		return this;
	}

	/**
	 * Sets a function that will be called to trigger a reactivity update.
	 */
	setTriggerRef(triggerFn: () => void) {
		this.#triggerRef = triggerFn;
		return this;
	}

	/**
	 * Returns the function that will be called to trigger a reactivity update.
	 */
	get triggerRef() {
		return this.#triggerRef;
	}

	/**
	 * Creates a promise that will be resolved when the offerImpressionUUID is updated.
	 * Recreates the promise on each impression.
	 * This promise prevents the clickout event from firing before the batch impression queue is processed, and the impression uuid is generated.
	 */
	createOfferImpressionUUIDPromise() {
		this.#offerImpressionUUIDDeferred = createDeferred<string>();
	}

	/**
	 * Sets the offerImpressionUUID for this element and resolves the deferred promise.
	 * This unblocks any pending clickout events waiting for the UUID via `Promise.race`.
	 *
	 * Even if a new impression fires afterward and recreates the promise,
	 * a pending clickout will still resolve the promise that was captured at the time of the click.
	 * That promise remains in memory and active until the associated `Promise.race` completes.
	 *
	 * Each clickout is associated with the most recent impression that existed at the moment the event was triggered.
	 */
	setOfferImpressionUUID(offerImpressionUUID: string) {
		this.#offerImpressionUUID = offerImpressionUUID;
		this.#offerImpressionUUIDDeferred.resolve(offerImpressionUUID);

		this.updateContext('LinkContext', 'link', {
			self: this.#clickoutUUID,
			impression: this.#offerImpressionUUID,
		});

		return this;
	}

	/** Sets a unique clickoutUUID on each clickout event */
	setClickoutUUID() {
		this.#clickoutUUID = generateUUIDv4();

		// Adds a link context to the Clickout URL for interlinking with the offer impression
		// If LinkContext already exists, it will be overwritten

		const linkContext = {
			self: this.#clickoutUUID,
			impression: this.#offerImpressionUUID,
		};

		this.#trackingContexts.LinkContext
			? this.updateContext('LinkContext', 'link', linkContext)
			: this.addContexts(new SnowplowLinkContext(linkContext));

		return this;
	}

	/**
	 * Helper to check if a context is an ExperimentContext.
	 * ExperimentContexts are excluded from clickout tracking.
	 */
	#isExperimentContext(context: SnowplowContext): boolean {
		return context.__name === 'ExperimentContext';
	}

	/**
	 * Helper for setting additional contexts, already includes `TrackingHelper.getAdditionalContexts`
	 *
	 * @param contexts Reactive single or array of SnowplowContexts to include in the URL
	 */
	addContexts(contexts: MaybeRefOrGetter<Maybe<SnowplowContext | SnowplowContext[]>> = []) {
		const newContexts = toValue(contexts);

		if (newContexts != null) {
			if (Array.isArray(newContexts)) {
				newContexts
					.filter(ctx => !this.#isExperimentContext(ctx))
					.forEach(ctx => Reflect.set(this.#trackingContexts, ctx.__name, ctx));
			} else if (!this.#isExperimentContext(newContexts)) {
				Reflect.set(this.#trackingContexts, newContexts.__name, newContexts);
			}
		}

		return this;
	}

	/**
	 * Modify the value of a property in a context.
	 * If the context is not found it does nothing but will console.warn in development.
	 * If the value is nullish it does nothing.
	 */
	updateContext<C extends keyof ContextClasses, K extends keyof ContextClasses[C]>(
		context: C,
		key: K,
		value: ContextClasses[C][K]
	) {
		if (context in this.#trackingContexts) {
			if (value != null) this.#trackingContexts[context][key] = value;
		} else if (process.env.NODE_ENV === 'development' || JW_CONFIG.DOMAIN.includes('moviecycle')) {
			console.warn(`Context ${context} is not available in this ClickoutUrl`);
		}

		/**
		 * This is a reference to the function passed through setTriggerRef()
		 * It is called here to trigger a reactivity update in Vue, as we are updating the cx param on the url.toString().
		 * This ensures the ClickoutUrl toString() method will return the updated URL in ClickoutAnchor.
		 */
		this.triggerRef?.();

		return this;
	}

	apply(transform: (url: ClickoutUrl) => ClickoutUrl) {
		return transform(this);
	}

	/** Returns the Clickout URL as a string. */
	toString() {
		if (this.#skipRedirect) return this.#url.get('r') ?? '';

		this.set('cx', encodeContexts(Object.values(this.#trackingContexts)));

		// for WOW, WOW Sport, Sky Go, Sky Sport (DE), and for CanalSeries (FR) we need GDPR information
		const country = this.get('uct_country')?.toLowerCase() as WebLocale;

		if (isEligibleForGdprConsent(country, this.providerId, this.bundleId)) {
			this.setOnOffer('gdpr', GDPR_WEB_LOCALES.includes(country) ? '1' : '0');
			this.setOnOffer('gdpr_consent', getTCString('ClickoutUrl') ?? NO_CONSENT_TC_STRING);
		}

		return this.#url.toString();
	}
}

function isGdprEligibleCountry(country: string): country is GDPRCountries {
	return country in GDPR_PROVIDERS_CONFIG;
}

function isEligibleForGdprConsent(country: string, providerId?: number, bundleId?: number | null): boolean {
	if (!isGdprEligibleCountry(country)) return false;

	const config = GDPR_PROVIDERS_CONFIG[country];
	if (!config) return false;

	const isGDPRProvider = !!providerId && config.providers.includes(providerId);
	const isGDPRBundle = !!bundleId && config.bundles.includes(bundleId);

	return isGDPRProvider || isGDPRBundle;
}

const MUTATION_TIMEOUT = 500 as const;

const { mutate, loading, onError } = useMutation<GetFinalClickoutUrlMutation, GetFinalClickoutUrlMutationVariables>(
	GetFinalClickoutUrlDocument
);
export const skipUCTCountries = [];

/** Given a UrlHelper, get the final redirect value that UCT would calculate and open it in a new tab. */
export async function handleClickoutUrl(clickoutUrl: UrlHelper, enable = false, preAffiliateRequired = false) {
	if (process.server && (process.env.NODE_ENV === 'development' || JW_CONFIG.DOMAIN === 'moviecycle.com')) {
		console.warn('[ClickoutHandler] Do not use Clickout Handler on the server!');
	}

	// Flush any pending batched impressions before handling the clickout URL, as we are awaiting the batch impression before tracking the clickout.
	await flushBatchedImpressions();

	if (isClickoutUrl(clickoutUrl)) verifyClickoutContexts(clickoutUrl);

	// If disabled globally or locally then don't skip UCT and use the original URL, also if skipRedirect is true.
	if (!enable || (isClickoutUrl(clickoutUrl) && clickoutUrl.hasSkipRedirect)) {
		/** Call trackClickout() before safeOpen() to ensure the clickoutUUID is up-to-date */
		if (isClickoutUrl(clickoutUrl)) trackClickout(clickoutUrl);
		safeOpen(clickoutUrl);
	} else {
		try {
			// prettier-ignore
			onError(error => { throw error });

			const windowReference = window.open(undefined, '_blank', 'nofollow sponsored popup=false');

			const mutation = await mutate({
				input: clickoutUrl.toString(),
				uct2input: isClickoutUrl(clickoutUrl) ? getUct2Input(clickoutUrl) : undefined,
			});
			if (mutation?.data == null) throw new Error('Mutation did not return any data.');

			const timeOut = setTimeout(() => {
				if (loading.value === true) throw new Error(`Took over ${MUTATION_TIMEOUT}ms to get final URL`);

				clearTimeout(timeOut);
			}, MUTATION_TIMEOUT);

			safeOpen(mutation.data.getFinalClickoutUrl.url, windowReference);
			if (isClickoutUrl(clickoutUrl)) {
				trackClickout(clickoutUrl, mutation.data.getFinalClickoutUrl.url);

				if (preAffiliateRequired && !clickoutUrl.preAffiliatedUrl) {
					captureMessageForSentry(
						`[ClickoutHelper] preAffiliatedStandardWebURL is not set.`,
						{ where: '[ClickoutHelper] handleClickout', meta: { clickoutUrl } },
						'error',
						{ UCT: 'Query' }
					);
				}
			}
		} catch (error: any) {
			captureMessageForSentry(
				`[ClickoutHelper] ${error?.message ?? 'Error getting the final Clickout URL.'}`,
				{ where: '[ClickoutHelper] handleClickout', error, meta: { clickoutUrl } },
				'error',
				{ UCT: 'Query' }
			);

			return clickoutUrl;
		}
	}
}

/** Safari on iOS may block `window.open()` made inside an async call so we use promises. */
export function safeOpen(url: UrlHelper | string, _windowReference?: Window | null) {
	const clickoutUrl = url.toString();

	// if argument 3 (AKA `windowFeatures`) has anything other than just 'noopener', it'll open as a popup
	const windowReference = _windowReference ?? window.open(undefined, '_blank', 'nofollow sponsored popup=false');

	if (windowReference) {
		windowReference.location = clickoutUrl;
	} else {
		window.open(clickoutUrl, '_blank', 'nofollow sponsored popup=false');

		captureMessageForSentry(
			`[SafeOpen] New tab opened without reference.`,
			{
				where: '[clickout-helper.ts] safeOpen()',
				error: new Error('New tab opened without reference.'),
				meta: { clickoutUrl },
			},
			'error',
			{ UCT: 'Window Open' }
		);
	}
}

function encodeContexts(contexts: SnowplowContext[]): string {
	const typeList = new Set<string>();

	const completeContext = {
		schema: 'iglu:com.snowplowanalytics.snowplow/contexts/jsonschema/1-0-0',
		data: contexts
			.filter(c => c != null)
			// We need to pass two instances of link_context, one for the offer impression and one for the clickout.
			.filter(({ schema }) => (!typeList.has(schema) || schema.includes('link_context')) && typeList.add(schema))
			.map(c => c.toObject()),
	};

	try {
		const contextString = JSON.stringify(completeContext);
		return base64EncodeUrl(btoa(decodeURIComponent(encodeURIComponent(contextString))));
	} catch (error) {
		console.error('[OfferHelper] encodeContexts: Failed to generate encoded context string: ', error);
		return '';
	}
}

/** Checks that the ClickoutURL has the required tracking contexts. */
function verifyClickoutContexts(clickoutUrl: ClickoutUrl) {
	if (
		clickoutUrl.get('uct_buybox') !== 'sports' &&
		clickoutUrl.contexts.TitleContext == null &&
		clickoutUrl.contexts.TitleContextGraphql == null
	) {
		captureMessageForSentry(
			`[ClickoutAnchor] Clickout URL is missing a title context.`,
			{
				where: '[ClickoutAnchor.vue] handleClick()',
				error: new Error('No title context found when required.'),
				meta: { clickoutUrl: clickoutUrl.toString },
			},
			'error',
			{ UCT: 'Contexts' }
		);
	}

	if (clickoutUrl.get('uct_buybox') === 'sports' && clickoutUrl.contexts.SportContext == null) {
		captureMessageForSentry(
			`[ClickoutAnchor] Clickout URL is missing a sport context.`,
			{
				where: '[ClickoutAnchor.vue] handleClick()',
				error: new Error('No sport context found when required.'),
				meta: { clickoutUrl },
			},
			'error',
			{ UCT: 'Contexts' }
		);
	}
}

export function trackClickout(url: ClickoutUrl, finalUrl?: string) {
	// Do not track sports clickouts
	if (url.get('uct_sport')) {
		return;
	}

	url.setClickoutUUID();

	if (finalUrl) {
		url.updateContext('ClickoutContextGraphql', 'deeplink', encodeURI(finalUrl));
	}

	// batch impression occurs in 1 second intervals. we expect useBatchImpressionTracking to update the offerImpressionUUID
	// if this fails, we still want to track the clickout
	// even though we are flushing the impression queue, I think this is still a useful pattern to ensure that the event is fired
	Promise.race([url.offerImpressionUUIDPromise, new Promise(resolve => setTimeout(resolve, 3000))]).finally(() => {
		TrackingHelper.trackEvent(
			'clickout',
			{
				action: url.contexts.ClickoutContextGraphql.monetizationType,
				label: url.providerClearName,
				property: url.contexts.ClickoutContextGraphql.presentationType,
				value: url.trackingProperties.offerLocation ?? OfferLocation.REGULAR_BUYBOX,
			},
			Object.values(url.contexts)
		);
	});
}

/**
 * Creates a deferred promise with external resolve and reject methods.
 * Useful when the resolution or rejection of the promise is controlled outside the initial scope.
 *
 * @returns Object containing:
 *  - promise: The actual Promise instance.
 *  - resolve: A function to resolve the promise.
 *  - reject: A function to reject the promise.
 */
function createDeferred<T>(): DeferredPromise<T> {
	let resolve!: (value: T) => void;
	let reject!: (reason?: any) => void;
	const promise = new Promise<T>((res, rej) => {
		resolve = res;
		reject = rej;
	});
	return { promise, resolve, reject };
}

function isValidMonetizationType(monetizationType: MonetizationType | undefined) {
	return monetizationType && Object.values(MonetizationType).includes(monetizationType);
}

function getUct2Input(clickoutUrl: ClickoutUrl): GetFinalClickoutUrlMutationVariables['uct2input'] | undefined {
	const country = clickoutUrl.get('uct_country');
	const packageID = clickoutUrl.providerId;
	const jwEntityID = clickoutUrl.titleSourceId;
	const monetizationType = clickoutUrl.provider?.monetizationType;

	// Check for required properties
	if (!country || !packageID || !jwEntityID) {
		return undefined;
	}

	return {
		country,
		packageID,
		jwEntityID,
		platform: Platform.Web,
		// Optional properties
		ct: clickoutUrl.get('uct_ct') ?? undefined,
		packageName: clickoutUrl.providerClearName,
		MonetizationType: isValidMonetizationType(monetizationType) ? monetizationType : undefined,
		isFreeTrial: clickoutUrl.isFreeTrial,
		userAgent: clickoutUrl.get('uct_ua') ?? undefined,
		partnerID: 1,
		pageType: clickoutUrl.get('uct_pt') ?? undefined,
		campaignTag: clickoutUrl.get('uct_ctag') ?? undefined,
		campaignTag2: clickoutUrl.get('uct_ctag2') ?? undefined,
		doNotTrack: true,
		clickoutType: clickoutUrl.contexts.ClickoutContextGraphql?.clickoutType,
		placement: clickoutUrl.contexts.ClickoutContextGraphql?.placement,
		noOffer: clickoutUrl.contexts.ClickoutContextGraphql?.noOffer,
		// Ad Server IDs
		adID: clickoutUrl.get('uct_aid') ?? undefined,
		adBidID: clickoutUrl.get('uct_bid') ?? undefined,
		adCampaignID: clickoutUrl.get('uct_cid') ?? undefined,
	};
}

export async function setFinalUrlOnClickoutUrl(clickoutUrl: ClickoutUrl) {
	if (!!clickoutUrl.deeplink) return false;

	const mutation = await mutate({
		input: clickoutUrl.toString(),
		uct2input: isClickoutUrl(clickoutUrl) ? getUct2Input(clickoutUrl) : undefined,
	});

	const data = mutation?.data?.getFinalClickoutUrl;

	if (data?.url) {
		clickoutUrl
			.setDeeplink(data.url)
			.updateContext('ClickoutContextGraphql', 'deeplink', data.url)
			.updateContext('ClickoutContextGraphql', 'isAffiliated', data.isAffiliated)
			.updateContext('ClickoutContextGraphql', 'isUCTError', data.isUCTError);

		if (data.isUCTError && !!data.uctError.length) {
			clickoutUrl.updateContext('ClickoutContextGraphql', 'UCTError', data.uctError);
		}
	}
}

onError((error: any) => {
	captureMessageForSentry(
		`[ClickoutHelper]'}`,
		{ where: '[ClickoutHelper] setFinalUrlOnClickoutUrl()', error },
		'error'
	);
});

export function getClickoutUrlContextsArray(clickoutUrl: ClickoutUrl | undefined | null): SnowplowContext[] {
	return Object.values(clickoutUrl?.contexts ?? {});
}
